<template>
  <InviteContactModal
    v-model:name="name"
    v-model:email="email"
    :name-disabled="nameDisabled"
    :email-disabled="emailDisabled"
    :show="show"
    :title="title"
    :on-cancel="handleOnCancel"
    :on-confirm="handleOnConfirm"
    :help-text="helpText"
    :error-banner-message="errorBannerMessage"
    :server-validation-errors="serverValidationErrors"
    :save-button-text="saveButtonText"
    :save-button-text-while-working="saveButtonTextWhileWorking"
  >
    <template #commonFields>
      <CreatableSelect
        v-model="name"
        :show-create-button="true"
        :placeholder="$t('Enter recruitment agency name')"
        :label="`${$t('Recruitment agency name')}${!nameDisabled ? '*' : ''}`"
        :loading="$apollo.loading"
        :options="recruiterAgencies"
        :search-error="searchError"
        :error="serverValidationErrors['name']"
        :disabled="nameDisabled"
        class="m-b-15"
        data-test="recruiter-name"
        @create="createMode = true"
        @select-option="handleSelect"
        @update:model-value="handleUpdateName"
      >
        <div
          class="tw-flex tw-justify-between tw-items-center tw-w-full"
          data-test="add-new-recruiter"
        >
          <div>{{ name }}</div>
          <div class="tw-text-gray-700">{{ $t('Add new recruitment agency') }}</div>
        </div>
        <template #option="{ option }">
          <div class="tw-flex tw-justify-between tw-items-center">
            <WdsAvatarLabel
              :path="getAvatarPath(option.avatar, option.id)"
              :initials="option.initials"
              :text="option.name"
              sm
            />
            <div class="tw-text-gray-600 tw-uppercase">{{ option.market }}</div>
          </div>
        </template>
        <template v-if="selectedOption.initials || selectedOption.avatar" #prefix>
          <WdsAvatar
            :initials="selectedOption.initials"
            :path="getAvatarPath(selectedOption.avatar, selectedOption.id)"
            xs
          />
        </template>
      </CreatableSelect>
      <div v-if="createMode" class="tw-mb-6">
        <label>{{ `${$t('Email address')}${!emailDisabled ? '*' : ''}` }}</label>
        <WdsInput
          v-model="email"
          :disabled="emailDisabled"
          required
          :error="serverValidationErrors['email']"
          data-test="email-input"
        />
        <WdsText v-if="createMode && !emailDisabled" secondary>
          {{ $t('Choose the email address of your main point of contact') }}
        </WdsText>
      </div>
    </template>
    <template #additionalFields>
      <WdsStack align-start justify-space-between gap-sm class="m-b-15">
        <div v-if="!manageWorkers" class="tw-w-1/2">
          <label>{{ $t('Fee') }}</label>
          <InputCurrency v-model="recruiterFee" :currency="'%'" placeholder="0" />
        </div>
        <div class="recruiter-input-ownership">
          <label>{{ $t('Ownership days') }}</label>
          <WdsInput
            placeholder="0"
            required
            type="number"
            :model-value="recruiterOwnershipDays"
            :error="serverValidationErrors['recruiterOwnershipDays']"
            data-test="ownership-days-input"
            @update:model-value="updateOwnership"
          />
        </div>
      </WdsStack>
      <div class="tw-mb-6">
        <label>{{ $t('Tags') }}</label>
        <TypeSelect
          :name="'tags'"
          tag-type="skills"
          :selected-in="tags"
          :show-typesuggestions="false"
          :can-add-unsupported-types="true"
        />
      </div>
      <div v-if="!messageDisabled" class="recruiter-input-message">
        <label>{{ $t('Message') }}</label>
        <WdsInputArea
          height="height"
          :placeholder="$t('Type your message here')"
          @update:model-value="updateMessage"
        />
      </div>
    </template>
  </InviteContactModal>
</template>

<script>
import gql from 'graphql-tag'
import {
  WdsInput,
  WdsStack,
  WdsAvatar,
  WdsInputArea,
  WdsAvatarLabel,
  WdsText,
} from '@worksome/wds-components'
import InviteContactModal from '@/components/Contacts/InviteContactModal.vue'
import TypeSelect from '@/components/TypeSelect.vue'
import { FormValidation } from '@/utils/formValidation'
import enums from '@/utils/enum'
import InputCurrency from '@/components/InputCurrency.vue'
import { getFeeAsIntegerAndFraction } from '@/components/utilities/numberFormatting'
import CreatableSelect from '@/components/CreatableSelect.vue'
import { getAvatarPath } from '@/utils/avatar'

export default {
  components: {
    CreatableSelect,
    InputCurrency,
    InviteContactModal,
    WdsInput,
    WdsAvatar,
    WdsInputArea,
    WdsStack,
    WdsAvatarLabel,
    WdsText,
    TypeSelect,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    onCancel: {
      type: Function,
      default: null,
    },
    onConfirm: {
      type: Function,
      default: null,
    },
    initialFee: {
      type: Number,
      default: null,
    },
    initialOwnership: {
      type: Number,
      default: null,
    },
    initialName: {
      type: String,
      default: null,
    },
    initialEmail: {
      type: String,
      default: null,
    },
    initialTags: {
      type: Array,
      default: () => [],
    },
    nameDisabled: {
      type: Boolean,
      default: false,
    },
    emailDisabled: {
      type: Boolean,
      default: false,
    },
    messageDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Modal Title
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Modal help text
     */
    helpText: {
      type: String,
      default: null,
    },
    /**
     * Error text to display if a generic error occurs
     */
    errorText: {
      type: String,
      default: 'An error occurred',
    },
    /**
     * Text to display on save button
     */
    saveButtonText: {
      type: String,
      default: undefined,
    },
    /**
     * Text to display on save button while working
     */
    saveButtonTextWhileWorking: {
      type: String,
      default: undefined,
    },
    companyRecruiterIds: {
      type: Array,
      default: [],
    },
    manageWorkers: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      name: this.initialName || '',
      email: this.initialEmail || '',
      serverValidationErrors: {},
      errorBannerMessage: '',
      recruiterFee: getFeeAsIntegerAndFraction(this.initialFee * 0.01),
      recruiterOwnershipDays: this.initialOwnership?.toString(),
      message: null,
      tags: [...this.initialTags],
      inputTags: null,
      recruiterAgencies: [],
      searchError: undefined,
      // Indicates whether a user is creating a brand-new recruitment agency or is inviting an existing agency
      createMode: true,
      selectedOption: {},
    }
  },
  watch: {
    name() {
      this.removeValidationError('name')
    },
    email() {
      this.removeValidationError('email')
    },
  },
  mounted() {
    if (!this.nameDisabled) {
      this.$apollo.queries.recruitersSearch.start()
    }
  },
  apollo: {
    recruitersSearch: {
      manual: true,
      skip: true,
      client: 'publicApi',
      query: gql`
        query recruiterAgencies($searchValue: String) {
          recruiters(search: $searchValue) {
            data {
              id
              name
              initials
              avatar
            }
          }
        }
      `,
      variables() {
        return {
          searchValue: this.name,
        }
      },
      debounce: 400,
      result({ data }) {
        if (!this.name) {
          this.recruiterAgencies = []
        } else {
          this.recruiterAgencies = data?.recruiters.data.filter(
            (recruiter) => !this.companyRecruiterIds.includes(recruiter.id),
          )
        }
      },
      error(error) {
        const { graphQLErrors } = error
        let graphQLErrorMessage = ''
        if (FormValidation.containsFormValidations(graphQLErrors)) {
          graphQLErrorMessage = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')
        } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
          graphQLErrorMessage = graphQLErrors[0].message
        }
        this.searchError = `${$t('An error occurred while searching.')} ${graphQLErrorMessage}`
      },
    },
  },
  methods: {
    getAvatarPath,
    resetForm() {
      this.name = this.initialName || ''
      this.email = this.initialEmail || ''
      this.serverValidationErrors = {}
      this.recruiterFee = getFeeAsIntegerAndFraction(this.initialFee * 0.01)
      this.recruiterOwnershipDays = this.initialOwnership || 0
      this.errorBannerMessage = ''
      this.tags = [...this.initialTags] || []
      this.createMode = true
      this.selectedOption = {}
    },
    handleUpdateName() {
      this.selectedOption = {}
      this.createMode = true
    },
    handleOnCancel() {
      this.resetForm()
      this.onCancel()
    },
    handleSelect(option) {
      this.selectedOption = { ...option }
      this.createMode = false
      this.name = option.name
    },
    getFeeAsDecimal(feeObj) {
      return parseFloat(`${feeObj.integer}.${feeObj.fraction}`) * 0.01
    },
    updateOwnership(value) {
      this.removeValidationError('recruiterOwnershipDays')
      this.recruiterOwnershipDays = value
    },
    updateMessage(value) {
      this.message = value
    },
    removeValidationError(errorName) {
      this.serverValidationErrors[`input.${errorName}`] = undefined
    },
    async handleOnConfirm() {
      this.errorBannerMessage = ''

      let recruiterOwnershipDays = null
      let recruiterFee = null
      let tags = null

      if (this.recruiterOwnershipDays) {
        recruiterOwnershipDays = parseInt(this.recruiterOwnershipDays, 10)
      }

      if (this.recruiterFee) {
        recruiterFee = this.getFeeAsDecimal(this.recruiterFee)
      }

      if (this.tags) {
        // eslint-disable-next-line prefer-destructuring -- Baseline eslint ignore please fix if working on this
        tags = this.tags
      }

      return this.onConfirm({
        recruiterOwnershipDays,
        recruiterFee,
        tags,
        email: this.email,
        name: this.name,
        message: this.message,
        createMode: this.createMode,
        id: this.selectedOption.id,
      })
        .then(() => {
          this.handleOnCancel()
        })
        .catch((error) => {
          const { graphQLErrors } = error
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.serverValidationErrors = FormValidation.getSingularGraphqlErrors(
              graphQLErrors,
              'input.',
            )
          } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
            this.errorBannerMessage = graphQLErrors[0].message
          } else {
            this.errorBannerMessage = this.errorText
            this.$store.commit('sendNotification', {
              type: enums.notification.types.ERROR,
              msg: this.errorText,
              sender: this,
              severity: enums.notification.severity.CRITICAL,
            })
          }
        })
    },
  },
}
</script>

<style scoped lang="scss">
.recruiter-input-fee {
  width: 150px;
}

.recruiter-input-ownership {
  width: 242px;
}

.recruiter-input-message {
  margin-bottom: 10px;
}
</style>
